import { safeGet } from '../../../utils/safeGet'
import { hasExportPermission } from '../../../utils/exportPermission'
import { get as _get } from '../../../utils/fetcher'
import { filterByWord, filterByIssuerCode, getIssuersList } from '../../../utils/filters'
import { mapDataForFairmatProductScore } from '../../../utils/drivers'

// EfficiencyTestsDetails.vue's results view — GET /api/efficiencyTests/{id}/result (confirmed by
// the user with a full JSON sample), replacing the legacy /api/insights-new/details/{id} call this
// module used to make. `comparables[]` items only carry `issuerCode` at the top level; `issuerName`
// comes from the nested `score.productKey.issuerName` when present. There is no per-comparable
// "under testing"/follow flag and no per-option compatibility split (a comparable here is always a
// whole product, never an individual investment option) — those pieces stay hidden in the view.
//
// Everything else DOES have real data (confirmed with a live, non-placeholder sample) — each
// product's `score` object is shaped exactly like the legacy insights-new response's `productScore`
// (`{ scoreResults: [{ show4Product, totalScore, driverDetails, ... }] }`), so both the distribution
// chart's score extraction AND the InsightsTable row-details drilldown (mapDataForFairmatProductScore)
// work unchanged against it. There is no "market segment / under distribution / under testing" split
// for the chart though (that was a ScoringProfiles-specific classification) — comparables all go in
// the "productsMarketSegment" bucket (plain histogram, no highlight line) and the single
// testedProduct goes in "productsUnderTesting" (as a one-item array, so it still gets
// ScoringDefinitionDistributionChart's highlighted plot line). "productsUnderDistribution" has no
// equivalent and stays empty.
function extractTotalScore(scoreObj) {
  const results = safeGet(() => scoreObj.scoreResults, [])
  const primary = results.find((x) => x && x.show4Product) || results[0]
  return safeGet(() => primary.totalScore, null)
}

function mapProduct(el, index) {
  return {
    index,
    productId: el.productId,
    issuerCode: el.issuerCode,
    issuerName: safeGet(() => el.score.productKey.issuerName, el.issuerCode),
    // Both keys point at the same value: `name` is what InsightsTable.vue's columns/links use,
    // `productName` is what utils/filters.js's filterByWord() hard-codes.
    name: el.name,
    productName: el.name,
    isin: el.isin,
    referenceDate: el.referenceDate,
    market: el.market,
    status: el.status,
    score: extractTotalScore(el.score),
    productScore: mapDataForFairmatProductScore(el.score),
  }
}

function mapComparables(array) {
  return (array || []).map((el, i) => mapProduct(el, i))
}

function mapTestedProduct(testedProduct) {
  if (!testedProduct) return null
  return { ...mapProduct(testedProduct, 0), isTestedProduct: true }
}

// The tested product is always shown as a table row, regardless of search/issuer filters.
function withTestedProduct(testedProduct, list) {
  const tested = mapTestedProduct(testedProduct)
  return tested ? [tested, ...list] : list
}

export default {
  namespaced: true,

  state: {
    result: null,
    filteredProducts: [],
  },

  mutations: {
    setResult(state, data) {
      state.result = data
    },
    setFilteredProducts(state, payload) {
      state.filteredProducts = payload
    },
  },

  actions: {
    resetState({ state }) {
      state.result = null
      state.filteredProducts = []
    },

    loadInsight({ commit, dispatch }, scoringProfileId) {
      dispatch('efficiencyTestsCommon/setLoading', true, { root: true })
      return new Promise((resolve, reject) => {
        _get(`/api/efficiencyTests/${scoringProfileId}/result`)
          .then((data) => {
            commit('setResult', data)
            dispatch('initFilteredProducts', data)
            resolve()
          })
          .catch((err) => {
            reject(err)
          })
          .finally(() => {
            dispatch('efficiencyTestsCommon/setLoading', false, { root: true })
          })
      })
    },

    initFilteredProducts({ commit }, result) {
      commit('setFilteredProducts', withTestedProduct(result.testedProduct, mapComparables(result.comparables)))
    },

    filteredProducts({ state, getters, commit }, filters) {
      let list = getters.getComparablesMapped

      if (filters.queryString !== '') {
        list = filterByWord(list, filters.queryString)
      }

      if (filters.issuers.length > 0) {
        list = filterByIssuerCode(list, filters.issuers)
      }

      commit('setFilteredProducts', withTestedProduct(safeGet(() => state.result.testedProduct, null), list))
    },
  },

  getters: {
    getResult: (state) => {
      return safeGet(() => state.result, null)
    },
    getTestedProduct: (state) => {
      return safeGet(() => state.result.testedProduct, null)
    },
    getCoverage: (state) => {
      return safeGet(() => state.result.coverage, {})
    },
    getSummary: (state) => {
      return safeGet(() => state.result.summary, {})
    },
    // The tested product's own score, as a fraction of 1 (the outcome is decided on this value, not on a rounded one).
    getTestedProductScore: (state) => {
      return extractTotalScore(safeGet(() => state.result.testedProduct.score, null))
    },
    // Raw comparables (not mapped, not filtered by the coverage filters) — DriversBubbleChart.vue.
    getComparables: (state) => {
      return safeGet(() => state.result.comparables, []) || []
    },
    // OKV-234: revisionIds of the comparables only, deduped and without empties — the `in` filter of
    // the FKE summary call (FkeNarrativeSummary).
    getComparablesRevisionIds: (state, getters) => {
      return [...new Set(getters.getComparables.map((c) => safeGet(() => c.revisionId, null)).filter(Boolean))]
    },
    getComparablesMapped: (state) => {
      return mapComparables(safeGet(() => state.result.comparables, []))
    },
    getFilteredProducts: (state) => {
      return safeGet(() => state.filteredProducts, [])
    },
    // Insight.vue / ScoringDefinitionDistributionChart.vue's expected { productsMarketSegment,
    // productsUnderTesting, productsUnderDistribution } shape — see the header note for how the new
    // response's data maps onto it.
    getInsightProducts: (state, getters) => {
      const testedProduct = mapTestedProduct(getters.getTestedProduct)
      return {
        // Excluded comparables are never scored: they took no part in the distribution.
        productsMarketSegment: getters.getComparablesMapped.filter((p) => p.status !== 'Excluded'),
        productsUnderTesting: testedProduct ? [testedProduct] : [],
        productsUnderDistribution: [],
      }
    },
    getExportPermission(state, getters, rootState, rootGetters) {
      return hasExportPermission(rootGetters.getPermissions)
    },
    getCoverageIssuersList: (state, getters) => {
      return getIssuersList(getters.getComparablesMapped)
    },
  },
}
